import React, { Fragment, memo, useCallback, useContext, useMemo, useState } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { colors } from 'src/theme'
import { CancelSmallMinor, NoteMinor } from '@shopify/polaris-icons'
import { WarningContainer } from 'src/components/shared/WarningContainer'
import { FundingInvoice, FundingSettingsBankAccountDetails } from 'src/interfaces/payment'
import { CancelFundingInvoice, DownloadFundingInvoice } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { useDispatch } from 'react-redux'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { PaymentInvoiceModal } from '../PaymentInvoiceModal'
import { US_BANK_DETAILS, PARA_BANK_DETAILS } from 'src/constants/payment/constants'
import { formatCurrencyFromCents } from 'src/utils/transformers'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  isOpen?: boolean
  currentInvoice: FundingInvoice | null
  bankAccountDetails?: FundingSettingsBankAccountDetails | null
  onClose(): void
  refreshFundingInvoice(): Promise<void>
  handleClearCurrentInvoice: () => void
}

const BankFundInProgressModalComponent = ({
  isOpen,
  currentInvoice,
  bankAccountDetails,
  onClose,
  refreshFundingInvoice,
  handleClearCurrentInvoice,
}: Props) => {
  const dispatch = useDispatch()

  const { reload } = useContext(ReloadWrapperContext)

  const [invoice, setInvoice] = useState<any>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const container = useMemo(() => document.getElementById('root'), [])

  const handleShowInvoice = useCallback(async () => {
    if (!currentInvoice) {
      return
    }

    setIsLoading(true)

    try {
      const buffer: any = await createAsyncAction(
        dispatch,
        DownloadFundingInvoice.request({
          invoiceId: currentInvoice.id,
        }),
      )

      const blob = new Blob([buffer], { type: 'application/pdf' })

      setInvoice(URL.createObjectURL(blob))
    } catch {
      showToast(
        'Generating your invoice—this may take up to 15 seconds. Please hold on and try again soon',
      )
    } finally {
      setIsLoading(false)
    }
  }, [currentInvoice, isLoading])

  const handleCancelInvoice = useCallback(async () => {
    if (!currentInvoice) {
      return
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        CancelFundingInvoice.request({ invoiceId: currentInvoice.id }),
      )

      showToast('Invoice successfully canceled!', { variant: 'success' })

      onClose()

      handleClearCurrentInvoice()
      refreshFundingInvoice()
      reload()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [currentInvoice, isLoading])

  const clearInvoice = useCallback(() => {
    setInvoice(null)
  }, [])

  const handleDownloadInvoice = useCallback(async () => {}, [invoice])

  const accountName = bankAccountDetails?.accountName || PARA_BANK_DETAILS.accountName
  const accountNumber = bankAccountDetails?.accountNumber || PARA_BANK_DETAILS.accountNumber
  const routingNumber = bankAccountDetails?.routingNumber || PARA_BANK_DETAILS.routingNumber
  const bankName = bankAccountDetails ? US_BANK_DETAILS.bankName : PARA_BANK_DETAILS.bankName
  const accountType = bankAccountDetails
    ? US_BANK_DETAILS.accountType
    : PARA_BANK_DETAILS.accountType
  const bankAddress = bankAccountDetails
    ? US_BANK_DETAILS.bankAddress
    : PARA_BANK_DETAILS.bankAddress

  return (
    <Fragment>
      <Modal
        container={container}
        open={Boolean(isOpen)}
        onClose={onClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        css={tw`flex justify-center items-center backdrop-blur p-4`}
      >
        <div
          data-testid="payments-main-fund-bank-in-progress-modal"
          css={tw`bg-white rounded-lg overflow-hidden w-[584px]`}
        >
          <div css={tw`p-4 pb-6 flex flex-col`}>
            <div
              data-testid="payments-main-fund-bank-in-progress-close-btn"
              css={tw`p-1 rounded bg-[#EDEDED] h-[28px] hover:bg-[#DAD9E0] active:bg-[#B4AFC0] cursor-pointer self-end`}
              onClick={onClose}
            >
              <CancelSmallMinor width={20} height={20} />
            </div>
            <GSIcon iconName="gs_clock_orange" css={tw`w-5 h-5 self-center`} />
            <Typography variant="h4" css={tw`mt-2 text-center mb-1`}>
              Waiting for your transfer
            </Typography>
            <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mx-24 text-center`}>
              Please transfer the funds using the bank details below to settle the invoice. Once we
              receive the settlement, we'll proceed with sending settlements to your contractors.
            </Typography>
            <div css={tw`self-center my-8`}>
              <Typography css={tw`mb-1 text-center`} variant="h2">
                {currentInvoice?.amount && formatCurrencyFromCents(currentInvoice.amount)}
              </Typography>
              <Typography css={tw`text-center`} variant="body2" color={colors.SHADES_GREY_400}>
                Total transfer
              </Typography>
            </div>
            <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`text-center`}>
              Please transfer the funds using the bank details provided to settle the invoice.
            </Typography>
            <div css={tw`p-4 rounded-2xl border border-solid border-[#EDEDED] mb-6 mt-4`}>
              <div
                css={tw`flex flex-row border-0 border-b border-solid border-[#EDEDED] pb-4 mb-4`}
              >
                <div css={tw`flex-1`}>
                  <Typography css={tw`mb-1`} variant="h6" color={colors.SHADES_GREY_400}>
                    Bank Name
                  </Typography>
                  <Typography variant="body2">{bankName}</Typography>
                </div>
                <div css={tw`flex-1`}>
                  <Typography css={tw`mb-1`} variant="h6" color={colors.SHADES_GREY_400}>
                    Account Name
                  </Typography>
                  <Typography variant="body2">{accountName}</Typography>
                </div>
                <div css={tw`flex-1`}>
                  <Typography css={tw`mb-1`} variant="h6" color={colors.SHADES_GREY_400}>
                    Account Number
                  </Typography>
                  <Typography variant="body2">{accountNumber}</Typography>
                </div>
              </div>
              <div
                css={tw`flex flex-row border-0 border-b border-solid border-[#EDEDED] pb-4 mb-4`}
              >
                <div css={tw`flex-1`}>
                  <Typography css={tw`mb-1`} variant="h6" color={colors.SHADES_GREY_400}>
                    Routing Number (ABA)
                  </Typography>
                  <Typography variant="body2">{routingNumber}</Typography>
                </div>
                <div css={tw`flex-1`}>
                  <Typography css={tw`mb-1`} variant="h6" color={colors.SHADES_GREY_400}>
                    Account Type
                  </Typography>
                  <Typography variant="body2">{accountType}</Typography>
                </div>
                <div css={tw`flex-1`}>
                  <Typography css={tw`mb-1`} variant="h6" color={colors.SHADES_GREY_400}>
                    Bank Address
                  </Typography>
                  <Typography variant="body2">{bankAddress}</Typography>
                </div>
              </div>
              <div css={tw`flex-1`}>
                <Typography css={tw`mb-1`} variant="h6" color={colors.SHADES_GREY_400}>
                  Reference
                </Typography>
                <Typography variant="body2">{currentInvoice?.invoiceIds?.join(', ')}</Typography>
              </div>
            </div>
            <WarningContainer
              title="Important"
              subtitle="You can't add or modify settlements while this invoice is pending. To make changes, please cancel the invoice and start the process again."
            />
          </div>
          <div
            css={tw`px-4 py-3 flex flex-row justify-between items-center border-0 border-t border-solid border-[#EDEDED]`}
          >
            <Button
              size="small"
              color="white"
              leftIcon={<NoteMinor width={20} />}
              onClick={handleShowInvoice}
              disabled={isLoading}
              data-testid="payments-main-fund-bank-in-progress-show-invoice-btn"
            >
              Show invoice
            </Button>
            <Button
              size="small"
              color="red"
              onClick={handleCancelInvoice}
              disabled={isLoading}
              data-testid="payments-main-fund-bank-in-progress-cancel-invoice-btn"
            >
              Cancel Invoice
            </Button>
          </div>
        </div>
      </Modal>
      <PaymentInvoiceModal
        isOpen={Boolean(invoice)}
        invoice={invoice}
        onClose={clearInvoice}
        onDownload={handleDownloadInvoice}
      />
    </Fragment>
  )
}

export const BankFundInProgressModal = memo(BankFundInProgressModalComponent)
